import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  HandCoins, MapPin, Clock, CalendarDays, Loader2, Check, Users as UsersIcon,
  X, ShieldAlert, Pill,
} from "lucide-react";
import { toast } from "sonner";

export default function OpenShifts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/shifts/open");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const bid = async (s) => {
    if (!window.confirm(`Bid for the ${formatDate(s.start_time)} shift at ${s.location_name || "site"}? An admin will review and notify you.`)) return;
    setBusyId(s.id);
    try {
      const r = await api.post(`/shifts/${s.id}/bid`);
      if (r.data?.auto_filled) {
        toast.success("You were the sole qualified bidder — shift auto-assigned to you!");
      } else {
        toast.success("Bid submitted — you'll be notified if approved");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      // Refresh so stale auto-filled/closed shifts drop out of the list.
      load();
    } finally { setBusyId(null); }
  };

  if (user.role === "admin") {
    return (
      <div className="accp-card p-6 max-w-xl">
        <div className="accp-label">Open shifts</div>
        <p className="text-sm text-muted-foreground mt-2">
          This page is the staff-facing bidding board. To open shifts for bids and review incoming bids,
          use the <a href="/shifts" className="text-primary underline">Shifts</a> admin page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="open-shifts-page">
      <div>
        <div className="accp-label">Open for bids</div>
        <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          <HandCoins size={28} className="inline mr-2 -mt-1" />
          Pick up a shift
        </h1>
        <p className="text-muted-foreground mt-1">
          Admins post open shifts here. Bid on any that fit your schedule; you'll get a push notification when a winner is chosen.
        </p>
        <p className="text-xs text-muted-foreground mt-1 italic">
          Auto-assigns to the sole qualified bidder after 12 hours if uncontested.
        </p>
      </div>

      {loading && (
        <div className="accp-card p-8 text-center text-muted-foreground" data-testid="open-shifts-loading">
          <Loader2 size={16} className="animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="accp-card p-10 text-center text-muted-foreground" data-testid="open-shifts-empty">
          <CalendarDays size={24} className="inline mr-2 text-primary" />
          No open shifts available right now. Check back later — admins post new ones as they come up.
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((s) => (
          <div key={s.id} className="accp-card p-5 flex flex-col gap-3" data-testid={`open-shift-${s.code}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="accp-label">{s.code}</div>
                <div className="text-lg font-semibold text-primary mt-1" style={{ fontFamily: "Outfit" }}>
                  {formatDate(s.start_time)}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Clock size={12} /> {formatTimeRange(s.start_time, s.end_time)} · {formatHours(s.start_time, s.end_time)}h
                </div>
                {s.location_name && (
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <MapPin size={12} /> {s.location_name}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {s.requires_medication && (
                  <span className="text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF8E6] text-[#7A5A00]">
                    <Pill size={10} /> CMT
                  </span>
                )}
                <span className="text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-primary"
                      data-testid={`bids-count-${s.code}`}>
                  <UsersIcon size={10} /> {s.bids_count} bid{s.bids_count === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            {s.notes && (
              <p className="text-xs text-muted-foreground border-l-2 border-border pl-2">
                {s.notes}
              </p>
            )}

            {s.i_bid ? (
              <div className="mt-auto rounded-xl compliance-valid p-2.5 text-xs font-semibold flex items-center gap-2"
                   data-testid={`bid-submitted-${s.code}`}>
                <Check size={14} /> Bid submitted — awaiting admin review
              </div>
            ) : (
              <button
                onClick={() => bid(s)}
                disabled={busyId === s.id}
                className="accp-btn-primary mt-auto"
                data-testid={`bid-btn-${s.code}`}
              >
                {busyId === s.id ? <Loader2 size={14} className="animate-spin" /> : <HandCoins size={14} />}
                Bid for this shift
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso?.slice(0, 10) || ""; }
}

function formatTimeRange(a, b) {
  const f = (x) => new Date(x).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  try { return `${f(a)}–${f(b)}`; } catch { return ""; }
}

function formatHours(a, b) {
  try {
    const ms = new Date(b) - new Date(a);
    return (ms / 36e5).toFixed(1);
  } catch { return "?"; }
}
